import { ApiClient } from './apiClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface OrderWithLinesDto {
  header: OrderDto;
  lines: OrderLineDto[];
}

export interface OrderDto {
  id: number;
  companyNo: string;
  orderNo: number;
  orderNoERP: string;
  orderStatus: number;
  pickListNoERP?: string;
  orderType?: string;
  orderStatusERP?: string;
  orderDate?: string;
  deliveryDate?: string;
  customerNo: string;
  customerName?: string;
  delAddressNo?: number;
  delCustomerName?: string;
  delAddress1?: string;
  delAddress2?: string;
  delAddress3?: string;
  delZipCode?: string;
  delCity?: string;
  delCountryCode?: string;
  delCountryName?: string;
  deliveryGLN?: string;
  customerOrderNo?: string;
  yourRef?: string;
  ourRef?: string;
  projectNo?: string;
  depNo?: string;
  deliveryMethod?: string;
  temsOfDelivery?: string;
  shipmentType?: string;
  carrierName?: string;
  termsOfPayment?: string;
  employee?: string;
  orderPriority?: number;
  customerPriority?: number;
  district?: string;
  contactName?: string;
  orderTotal?: number;
  customerType?: string;
  customerGrp1?: string;
  customerGrp2?: string;
  customerGrp3?: string;
  isRemainderYesNo?: boolean;
  createRemainderYesNo?: boolean;
  emailAddress?: string;
  orderDeliveryYear?: number;
  orderDeliveryMonth?: number;
  orderDeliveryDay?: number;
  orderDeliveryWeek?: number;
  orderFreeField1?: string;
  orderFreeField2?: string;
  orderFreeField3?: string;
  orderFreeField4?: string;
  orderFreeField5?: string;
  orderFreeField6?: string;
  orderFreeField7?: string;
  orderFreeField8?: string;
  orderFreeField9?: string;
  orderFreeField10?: string;
  orderFreeField11?: string;
  orderFreeField12?: string;
  orderFreeField13?: string;
  orderFreeField14?: string;
  orderFreeField15?: string;
  orderFreeField16?: string;
  customerDeliveryDay?: string;
  telephone?: string;
  mobTelephone?: string;
  tripID?: number;
  districtNo?: number;
  eventCustomerRef1?: string;
  eventCustomerRef2?: string;
  eventCustomerRef3?: string;
  emailNotificationAddress?: string;
  smsNotificationAddress?: string;
  intEmailNotificationAddress?: string;
  intSMSNotificationAddress?: string;
  distributionYesNo?: boolean;
  deliveryAlias?: string;
  filterGroupID?: string;
  pListFreeField1?: string;
  pListFreeField2?: string;
  pListFreeField3?: string;
  shipmentID?: number;
  clientID?: string;
  loadingName?: string;
  loadingAddress1?: string;
  loadingZipCode?: string;
  loadingCity?: string;
  loadingCountry?: string;
  dropPointID?: string;
  warehouse?: string;
  arrivalDate?: string;
  deliveryMethodNo?: number;
  consignmentSWProductNo?: string;
  updatedBy?: string;
  lastUpdate?: string;
  createdBy?: string;
  created?: string;
  statusText: string;
}

export interface OrderLineDto {
  id: number;
  companyNo: string;
  orderNo: number;
  lineNo: number;
  lineNoERP?: number;
  lineStatus: number;
  pListID?: number;
  itemNo: string;
  itemBarCode?: string;
  description1?: string;
  itemGroup1?: string;
  itemGroup2?: string;
  itemGroup3?: string;
  quantity?: number;
  quantityDelivered?: number;
  quantityPicked?: number;
  itemUnitCode?: string;
  itemUnitTxt?: string;
  price?: number;
  discount?: number;
  deliveryDate?: string;
  serialNoItemYesNo?: boolean;
  stdLocationName?: string;
  pickZone?: string;
  warehouse?: string;
  batchID?: string;
  expirationDate?: string;
  weight?: number;
  height?: number;
  length?: number;
  width?: number;
  volum?: number;
  itemPictureName?: string;
  quantityReserved?: number;
  lineStatus2?: number;
  startDateTime?: string;
  stopDateTime?: string;
  quantityAvailable?: number;
  operatorTxt?: string;
  approveQuantityDeviation?: boolean;
  changeQuantityOnDeviation?: boolean;
  pListBatchYesNo?: boolean;
  packageNo?: number;
  visiblePackageNo?: number;
  useSTAND005?: boolean;
  orderLineFreeField1?: string;
  orderLineFreeField2?: string;
  orderLineFreeField3?: string;
  orderLineFreeField4?: string;
  orderLineFreeField5?: string;
  orderLineFreeField6?: string;
  orderLineFreeField7?: string;
  orderLineFreeField8?: string;
  orderLineFreeField9?: string;
  orderLineFreeField10?: string;
  isDisplayPallet?: boolean;
  qtyOnPallet?: number;
  printSTAND005?: boolean;
  itemTemperatureInfo?: string;
  stackingWeight?: number;
  memo1?: string;
  serverStatus?: number;
  stcZoneID?: string;
  binGrpNo?: number;
  resourceNo?: number;
  quantityGross?: number;
  clientID?: string;
  externalSystemID?: number;
  externalSystemStatus?: number;
  pListIDSplit?: number;
  unCode?: string;
  pickListMishandledByOperator?: boolean;
  buyersItemNo?: string;
  subLineNoERP?: number;
  orderNoERP?: string;
  extraQuantityPickedYesNo?: boolean;
  batchItemYesNo?: boolean;
  pickedBy?: string;
  updatedBy?: string;
  lastUpdate?: string;
  createdBy?: string;
  created?: string;
  statusText: string;
  remainingQuantity?: number;
}

class OrdersService {
  /**
   * Get new orders (OrderStatus = 0 / Ubehandlet) with unassigned order lines (PListID = 0)
   */
  async getNewOrdersWithLines(): Promise<OrderWithLinesDto[]> {
    const response = await ApiClient.get(`${API_BASE_URL}/orders/newOrders/with-lines`);

    if (!response.ok) {
      throw new Error(`Failed to fetch orders with lines: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get new/unprocessed orders with lines (OrderStatus = 0 / Ubehandlet)
   * @deprecated Use getNewOrdersWithLines() instead for clarity
   */
  async getOpenOrdersWithLines(): Promise<OrderWithLinesDto[]> {
    return this.getNewOrdersWithLines();
  }

  /**
   * Get order status text
   */
  getOrderStatusText(status: number): string {
    switch (status) {
      case 0: return 'Ubehandlet';
      case 10: return 'Åpen';
      case 20: return 'Klar til plukking';
      case 30: return 'Under plukking';
      case 40: return 'Plukket';
      case 50: return 'Pakket';
      case 60: return 'Levert';
      case 70: return 'Fakturert';
      case 80: return 'Kansellert';
      default: return `Status ${status}`;
    }
  }

  /**
   * Get order line status text
   */
  getOrderLineStatusText(status: number): string {
    switch (status) {
      case 0: return 'Ubehandlet';
      case 10: return 'Åpen';
      case 20: return 'Reservert';
      case 30: return 'Plukket';
      case 40: return 'Levert';
      case 50: return 'Fakturert';
      case 60: return 'Kansellert';
      default: return `Status ${status}`;
    }
  }

  /**
   * Format date for display
   */
  formatDate(dateString?: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('no-NO');
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount?: number): string {
    if (amount === undefined || amount === null) return '';
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Format quantity for display
   */
  formatQuantity(quantity?: number): string {
    if (quantity === undefined || quantity === null) return '';
    return new Intl.NumberFormat('no-NO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    }).format(quantity);
  }
}

export const ordersService = new OrdersService();
